'use client';

import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Clock, ArrowRight } from "lucide-react";
import RichText from "@/components/ui/rich-text";
import { formatTextAsHTMLList, ensureRequirementsList } from '@/components/ui/format-to-html-list';
import { Job } from "@/types/career";

const parseListContent = (text: string): string[] => {
  return text
    .split('\n')
    .map(line => line.replace(/^[\s\-•*·.]+/, '').trim())
    .filter(Boolean);
};

export default function JobDetailContent({ job }: { job: Job }) {
  const requirements = parseListContent(job.requirement || '');

  return (
    <div className="bg-brand-text min-h-screen">
      <div className="lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px] mx-auto px-4 lg:px-0 py-5 sm:py-20 xl:py-24">

        {/* Back link */}
        <motion.div
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link
            href="/career"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-brand-bg text-sm font-medium transition-colors mb-12 group 3xl:text-xl"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
            All open roles
          </Link>
        </motion.div>

        {/* ── Header ───────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="mb-12 pb-12 border-b border-brand-bg/[0.08] flex flex-col items-center text-center"
        >
          {job.department && (
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-bg/35 mb-4 3xl:text-base">
              {job.department}
            </p>
          )}

          <h1 className="text-[clamp(1.9rem,5vw,3.2rem)] font-bold text-brand-bg leading-[1.05] tracking-tight mb-6 3xl:text-6xl">
            {job.title}
          </h1>

          {/* Meta chips */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {job.location && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-bg/[0.06] border border-brand-bg/[0.08] px-3 py-1.5 text-xs font-medium text-brand-bg/80 3xl:text-sm">
                <MapPin className="h-3 w-3" />
                {job.location}
              </span>
            )}
            {job.employment_type && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-bg/[0.06] border border-brand-bg/[0.08] px-3 py-1.5 text-xs font-medium text-brand-bg/80 3xl:text-sm">
                <Clock className="h-3 w-3" />
                {job.employment_type}
              </span>
            )}
            {(job.min_salary || job.max_salary) && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-bg/[0.06] border border-brand-bg/[0.08] px-3 py-1.5 text-xs font-semibold text-brand-bg/80 3xl:text-base">
                RM{" "}
                {job.min_salary && Number(job.min_salary).toLocaleString()}
                {job.min_salary && job.max_salary ? " – " : ""}
                {job.max_salary && Number(job.max_salary).toLocaleString()}
              </span>
            )}
          </div>

          {/* Apply button */}
          <Link
            href={`/career/${job.slug}/apply`}
            className="group inline-flex items-center gap-2 rounded-full bg-brand-grey text-brand-card px-7 py-3 text-sm font-semibold transition-all duration-200 3xl:text-xl 3xl:px-9 3xl:py-4"
          >
            Apply for this role
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
        </motion.div>

        {/* ── Content sections ─────────────────────────────────────── */}
        <div className="space-y-10 text-brand-bg">
          {job.description && (
            <motion.section
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="border-b border-brand-bg/[0.08]"
            >
              <p className="text-lg font-bold uppercase tracking-[0.22em] text-brand-bg/35 mb-5 3xl:text-xl">
                About the role
              </p>
              <RichText content={job.description} />
            </motion.section>
          )}

          {requirements.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="border-b border-brand-bg/[0.08]"
            >
              <p className="text-lg font-bold uppercase tracking-[0.22em] text-brand-bg/35 mb-5 3xl:text-xl">
                Requirements
              </p>
              <RichText content={ensureRequirementsList(job.requirement)} />
            </motion.section>
          )}

          {job.skill && (
            <motion.section
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <p className="text-lg font-bold uppercase tracking-[0.22em] text-brand-bg/35 mb-5 3xl:text-xl">
                Skills &amp; technologies
              </p>
              <RichText content={formatTextAsHTMLList(job.skill)} />
            </motion.section>
          )}
        </div>

      </div>
    </div>
  );
}
